<template>
  <div class="custom-slider default"
       :data-cy="cypressName">
    <input
      ref="slider"
      :value="sliderValue"
      @input="({ target }) => (sliderValue = parseFloat(target.value))"
      type="range"
      :min="min"
      :max="max"
      class="slider"
    />
    <div v-if="customLabel" class="marks-wrapper">
      <div class="marks" v-for="label in customLabel">
          <div class="mark-label"
               v-html="label.icon"
               :style="[label.position == 100 ? {'right': '0'} : {'left': label.position}]"
               :class="{'selected': label.value <= sliderValue}"
               @click.stop="setValue(label)"
               >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, watchEffect } from "vue"

const props = defineProps({
  min: {
    type: Number,
    required: false
  },
  max: {
    type: Number,
    required: false
  },
  value: {
    required: false,
    type:[String,Number]
  },
  data: {
    type: Array,
    required: false
  },
  customLabel: {
    type: Object,
    required: false
  },
  labelFilter: {
    type: Function,
    required: false
  },
  cypressName: {
    type: String,
    required: false
  }
})

const emit = defineEmits(['input'])

const sliderValue = ref(props.value)
const slider = ref(null)

// function to get the progress of the slider
const getProgress = (value, min, max) => {
  return Math.min(((value - min) / (max - min)) * 100, 100)
}

// function to set the css variable for the progress
const setStylesOfProgress = (progress) => {
  slider.value.style.setProperty("--ProgressPercent", `${progress}%`)
}

const setValue = (label) => {
  sliderValue.value = label.value
}

watch(() => props.value, () => {
  sliderValue.value = props.value
})

watchEffect(() => {
  if (slider.value) {
    emit("input", sliderValue.value.toString())

    const progress = getProgress (
      sliderValue.value,
      slider.value.min,
      slider.value.max
    )

    setStylesOfProgress(progress)
  }
})
</script>

<style lang="scss">
.custom-slider {
  --trackHeight: 6px;
  --thumbRadius: 28px;
}

/* style the input element with type "range" */
.custom-slider input[type="range"] {
  position: relative;
  appearance: none;
  background: none;
  border-radius: 15px;
  z-index: 0;
  height: 6px;
  width: 100%;
  cursor: pointer;
}

/* focus state for accessibility */
.custom-slider input[type="range"]:focus {
  outline: 2px solid $blue;
  outline-offset: 8px;
}

/* ::before element to replace the slider track */
.custom-slider input[type="range"]::before {
  content: "";
  display: block;
  position: absolute;
  width: var(--ProgressPercent, 100%);
  height: 6px;
  background: $slider-process;
  border-radius: 999px;
}

/* `::-webkit-slider-runnable-track` targets the track (background) of a range slider in chrome and safari browsers. */
.custom-slider input[type="range"]::-webkit-slider-runnable-track {
  appearance: none;
  background: $slider-background;
  height: var(--trackHeight);
  border-radius: 999px;
}

.custom-slider input[type="range"]::-webkit-slider-thumb {
  position: relative;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  background: $slider-thumb-center;
  border: 8px solid $slider-thumb;
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  z-index: 1;
}

/* `::-moz-range-track` targets the track (background) of a range slider in Mozilla Firefox. */
.custom-slider.default input[type="range"]::-moz-range-track {
  appearance: none;
  background: $slider-background;
  height: var(--trackHeight);
  border-radius: 999px;
}

.custom-slider input[type="range"]::-moz-range-thumb {
  position: relative;
  box-sizing: border-box;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  background: $slider-background;
  border: 8px solid $slider-thumb;
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  z-index: 1;
}

.custom-slider input[type="range"]::-moz-range-progress {
  background: $slider-process;
  border-radius: 999px;
  height: var(--trackHeight);
}

.marks-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: end;

  .marks {
    cursor: pointer;

    .mark-label {
      color: $gray-4d;
      font-size: 14px;

      i.vf-icon {
        font-size: 30px;
        color: $black;
      }

      strong {
        color: $gray-4d;
        white-space: nowrap;
      }
    }

    &:first-child {
      color: $black;
    }
  }
}

</style>
